// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: layout preferences stay on this device; the active Chats filter uses
// the signed-in user's synced preferences. Search remains session-only.

import {
	type ChatsPanelFilter,
	type ChatsPanelSource,
	normalizeFilter,
	parseFilterSources,
} from '@app/features/channel/utils/ChatsPanelModel';
import AppStorage from '@app/features/platform/state/PersistentStorage';
import {Logger} from '@app/features/platform/utils/AppLogger';
import UserSettings from '@app/features/user/state/UserSettings';
import type {MediaProxyImageSize} from '@fluxer/constants/src/MediaProxyImageSizes';
import {makeAutoObservable} from 'mobx';

export type {ChatsPanelFilter} from '@app/features/channel/utils/ChatsPanelModel';
export type ChatsPanelFilterSource = ChatsPanelSource;
const logger = new Logger('ChatsPanel');
export type ChatsPanelSectionId = 'pinned' | 'recent' | 'quiet';

/** Days a chat may sit idle before it folds into Quiet. 0 means never fold. */
export const QUIET_AFTER_DAYS_OPTIONS: ReadonlyArray<number> = [3, 7, 14, 30, 0];
export const QUIET_AFTER_DAYS_DEFAULT = 7;

const STORAGE_KEY = 'konvo:chats-panel:v1';

interface PersistedChatsPanel {
	compactDesktop: boolean;
	quietAfterDays: number;
	showMail: boolean;
	showWhatsApp: boolean;
	showTeams: boolean;
	collapsed: Record<ChatsPanelSectionId, boolean>;
	mailExpanded: boolean;
	quietWhatsAppExpanded: boolean;
}

const DEFAULTS: PersistedChatsPanel = {
	compactDesktop: false,
	quietAfterDays: QUIET_AFTER_DAYS_DEFAULT,
	showMail: true,
	showWhatsApp: true,
	showTeams: true,
	collapsed: {pinned: false, recent: false, quiet: true},
	mailExpanded: false,
	quietWhatsAppExpanded: false,
};

function readPersisted(): PersistedChatsPanel {
	const raw = AppStorage.getItem(STORAGE_KEY);
	if (!raw) return {...DEFAULTS, collapsed: {...DEFAULTS.collapsed}};
	try {
		const parsed = JSON.parse(raw) as Partial<PersistedChatsPanel>;
		const quietAfterDays =
			typeof parsed.quietAfterDays === 'number' && QUIET_AFTER_DAYS_OPTIONS.includes(parsed.quietAfterDays)
				? parsed.quietAfterDays
				: DEFAULTS.quietAfterDays;
		return {
			compactDesktop: parsed.compactDesktop === true,
			quietAfterDays,
			showMail: parsed.showMail ?? DEFAULTS.showMail,
			showWhatsApp: parsed.showWhatsApp ?? DEFAULTS.showWhatsApp,
			showTeams: parsed.showTeams ?? DEFAULTS.showTeams,
			collapsed: {...DEFAULTS.collapsed, ...(parsed.collapsed ?? {})},
			mailExpanded: parsed.mailExpanded ?? DEFAULTS.mailExpanded,
			quietWhatsAppExpanded: parsed.quietWhatsAppExpanded ?? DEFAULTS.quietWhatsAppExpanded,
		};
	} catch {
		return {...DEFAULTS, collapsed: {...DEFAULTS.collapsed}};
	}
}

export class ChatsPanelStore {
	compactDesktop: boolean = DEFAULTS.compactDesktop;
	quietAfterDays: number = DEFAULTS.quietAfterDays;
	showMail: boolean = DEFAULTS.showMail;
	showWhatsApp: boolean = DEFAULTS.showWhatsApp;
	showTeams: boolean = DEFAULTS.showTeams;
	collapsed: Record<ChatsPanelSectionId, boolean> = {...DEFAULTS.collapsed};
	mailExpanded: boolean = DEFAULTS.mailExpanded;
	quietWhatsAppExpanded: boolean = DEFAULTS.quietWhatsAppExpanded;
	/** Reactive to local changes, account hydration and settings updates from other devices. */
	get filter(): ChatsPanelFilter {
		return normalizeFilter(UserSettings.getSubPreference('chatsFilter'));
	}
	/** The sources a chip selection covers; empty for 'all' and 'unread'. */
	get selectedSources(): Array<ChatsPanelFilterSource> {
		return parseFilterSources(this.filter);
	}
	/** True when exactly one source is selected (so `filter` is that source). */
	get isSingleSource(): boolean {
		return this.selectedSources.length === 1;
	}
	/** Desktop row avatars: small beside a one-line compact row, 44px otherwise. */
	get desktopAvatarSize(): MediaProxyImageSize {
		return this.compactDesktop ? 28 : 44;
	}
	/** Session only: the inline search box. */
	query = '';

	constructor() {
		this.applyPersisted(readPersisted());
		makeAutoObservable(this, {}, {autoBind: true});
		AppStorage.subscribe(() => this.applyPersisted(readPersisted()), {key: STORAGE_KEY, source: 'external'});
	}

	private applyPersisted(next: PersistedChatsPanel): void {
		this.compactDesktop = next.compactDesktop;
		this.quietAfterDays = next.quietAfterDays;
		this.showMail = next.showMail;
		this.showWhatsApp = next.showWhatsApp;
		this.showTeams = next.showTeams;
		this.collapsed = {...next.collapsed};
		this.mailExpanded = next.mailExpanded;
		this.quietWhatsAppExpanded = next.quietWhatsAppExpanded;
	}

	private persist(): void {
		const snapshot: PersistedChatsPanel = {
			compactDesktop: this.compactDesktop,
			quietAfterDays: this.quietAfterDays,
			showMail: this.showMail,
			showWhatsApp: this.showWhatsApp,
			showTeams: this.showTeams,
			collapsed: {...this.collapsed},
			mailExpanded: this.mailExpanded,
			quietWhatsAppExpanded: this.quietWhatsAppExpanded,
		};
		AppStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
	}

	setCompactDesktop(value: boolean): void {
		this.compactDesktop = value;
		this.persist();
	}

	setQuietAfterDays(days: number): void {
		this.quietAfterDays = QUIET_AFTER_DAYS_OPTIONS.includes(days) ? days : QUIET_AFTER_DAYS_DEFAULT;
		this.persist();
	}

	setShowMail(value: boolean): void {
		this.showMail = value;
		if (!value && this.selectedSources.includes('mail')) this.toggleSourceInSelection('mail');
		this.persist();
	}

	setShowWhatsApp(value: boolean): void {
		this.showWhatsApp = value;
		if (!value && this.selectedSources.includes('whatsapp')) this.toggleSourceInSelection('whatsapp');
		this.persist();
	}

	setShowTeams(value: boolean): void {
		this.showTeams = value;
		if (!value && this.selectedSources.includes('teams')) this.toggleSourceInSelection('teams');
		this.persist();
	}

	toggleSection(id: ChatsPanelSectionId): void {
		this.collapsed = {...this.collapsed, [id]: !this.collapsed[id]};
		this.persist();
	}

	toggleMailExpanded(): void {
		this.mailExpanded = !this.mailExpanded;
		this.persist();
	}

	toggleQuietWhatsAppExpanded(): void {
		this.quietWhatsAppExpanded = !this.quietWhatsAppExpanded;
		this.persist();
	}

	setFilter(filter: ChatsPanelFilter): void {
		if (filter !== 'all' && filter !== 'unread' && parseFilterSources(filter).length === 0) return;
		const next = normalizeFilter(filter);
		void UserSettings.setSubPreference('chatsFilter', next).catch((error) => {
			// The sync engine keeps the local selection dirty for a later retry.
			logger.warn('Could not sync the Chats filter:', error);
		});
	}

	/** Tap on a chip: show only this source. */
	selectSource(source: ChatsPanelFilterSource): void {
		this.setFilter(source);
	}

	/**
	 * Long press on a chip: add the source to, or drop it from, the current
	 * selection. Dropping the last source returns to 'all'.
	 */
	toggleSourceInSelection(source: ChatsPanelFilterSource): void {
		const current = this.selectedSources;
		const next = current.includes(source) ? current.filter((item) => item !== source) : [...current, source];
		this.setFilter(next.length === 0 ? 'all' : (next.join(',') as ChatsPanelFilter));
	}

	clearFilter(): void {
		this.setFilter('all');
	}

	setQuery(query: string): void {
		this.query = query;
	}
}

export default new ChatsPanelStore();
